import React from "react";
import { useAuth } from "@/hooks/use-auth";
import { useLanguage } from "@/hooks/use-language";
import { useEvents } from "@/hooks/use-events";
import { usePenalty } from "@/hooks/use-penalty";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PenaltyNotice } from "@/components/ui/penalty-notice";
import { formatPenaltyReason, isPenaltyActive } from "@/types/penalty";
import { AlertTriangle, Clock, XCircle, Calendar, MapPin, Users } from "lucide-react";
import { ProfileHeader } from "@/components/profile/ProfileHeader";
import { ProfileStats } from "@/components/profile/ProfileStats";
import { format } from "date-fns";
import { useSupabase } from "@/contexts/supabase/SupabaseClientProvider";
import { useNavigate } from "react-router-dom";
import { RatingHistoryChart } from "@/components/stats/RatingHistoryChart";

export default function Profile() {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const { events, cancel, leaveWaitingList } = useEvents();
  const { getMemberPenalty } = usePenalty();
  const navigate = useNavigate();
  const supabase = useSupabase();
  const [myGroups, setMyGroups] = React.useState<Array<{ id: string; name: string; parent_id?: string | null }>>([]);

  // Event color utility function matching Dashboard
  const getEventColor = (type: string) => {
    switch (type) {
      case 'tournament':
        return 'border-l-green-500 bg-green-50 hover:bg-green-100';
      case 'training':
        return 'border-l-blue-500 bg-blue-50 hover:bg-blue-100';
      case 'social':
        return 'border-l-purple-500 bg-purple-50 hover:bg-purple-100';
      default:
        return 'border-l-gray-500 bg-gray-50 hover:bg-gray-100';
    }
  };

  // Load user's groups from Supabase (UUID users). For dev (non-UUID), show a sensible default.
  React.useEffect(() => {
    const isUuid = (v: string) => /^(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i.test(v);
    (async () => {
      if (!user) {
        setMyGroups([]);
        return;
      }

      try {
        if (!isUuid(user.id)) {
          setMyGroups([
            { id: '550e8400-e29b-41d4-a716-446655440001', name: 'Main Padel Tennis Group', parent_id: null },
          ]);
          return;
        }
        const { data: gm } = await supabase
          .from('group_members')
          .select('group_id')
          .eq('user_id', user.id);
        const groupIds = (gm || []).map((r: any) => r.group_id);
        if (!groupIds.length) {
          setMyGroups([]);
          return;
        }
        const { data: groups } = await supabase
          .from('groups')
          .select('id,name,parent_id')
          .in('id', groupIds);
        setMyGroups(groups || []);
      } catch (error) {
        console.error('Failed to load user groups', error);
        setMyGroups([]);
      }
    })();
  }, [user]);

  if (!user) {
    return null;
  }

  const userPenalty = getMemberPenalty(user.id);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const signedEvents = events.filter((e) => e.participants.includes(user.id) && new Date(e.date) >= today);
  const waitingListEvents = events.filter((e) => (e.waitingList || []).includes(user.id) && new Date(e.date) >= today);

  return (
    <div className="space-y-8 max-w-7xl mx-auto">
      {/* Profile Header */}
      <ProfileHeader />

      {/* Stats Overview */}
      <ProfileStats />

      {/* Main Content with Tabs */}
      <Tabs defaultValue="events" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="events" className="gap-2">
            <Calendar className="h-4 w-4" />
            {language === 'no' ? 'Arrangementer' : 'Events'}
          </TabsTrigger>
          <TabsTrigger value="penalties" className="gap-2">
            <AlertTriangle className="h-4 w-4" />
            {language === 'no' ? 'Straff' : 'Penalties'}
          </TabsTrigger>
          <TabsTrigger value="groups" className="gap-2">
            <Users className="h-4 w-4" />
            {language === 'no' ? 'Grupper' : 'Groups'}
          </TabsTrigger>
        </TabsList>

        {/* Events Tab */}
        <TabsContent value="events" className="space-y-6">
          {/* Active Events */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground">
              {language === 'no' ? 'Aktive Påmeldinger' : 'Active Signups'} 
              {signedEvents.length > 0 && (
                <Badge variant="secondary" className="ml-2">{signedEvents.length}</Badge>
              )}
            </h3>
            {signedEvents.length === 0 ? (
              <Card className="bg-muted/20">
                <CardContent className="py-8 text-center">
                  <Calendar className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                  <p className="text-muted-foreground">{t("profile.no.events")}</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {signedEvents.map((ev) => {
                  const colorClasses = getEventColor(ev.type);
                  return (
                    <Card 
                      key={ev.id} 
                      className={`border-l-4 transition-all duration-200 cursor-pointer ${colorClasses}`}
                      onClick={() => navigate(`/event/${ev.id}`)}
                    >
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div>
                            <CardTitle className="text-lg hover:text-primary transition-colors">{ev.title}</CardTitle>
                          <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <Calendar className="h-4 w-4" />
                              {format(new Date(ev.date), 'PPP')}
                            </div>
                            <div className="flex items-center gap-1">
                              <Clock className="h-4 w-4 shrink-0" />
                              {ev.time.slice(0, 5)}
                            </div>
                            {ev.location && (
                              <div className="flex items-center gap-1">
                                <MapPin className="h-4 w-4" />
                                {ev.location}
                              </div>
                            )}
                          </div>
                        </div>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={(e) => {
                            e.stopPropagation();
                            cancel(ev.id, user.id);
                          }}
                          className="shrink-0"
                        >
                          {t("event.cancel.button")}
                        </Button>
                      </div>
                    </CardHeader>
                  </Card>
                  );
                })}
              </div>
            )}
          </div>

          {/* Waiting List */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground">
              {language === 'no' ? 'Venteliste' : 'Waiting List'}
              {waitingListEvents.length > 0 && (
                <Badge variant="outline" className="ml-2">{waitingListEvents.length}</Badge>
              )}
            </h3>
            {waitingListEvents.length === 0 ? (
              <Card className="bg-muted/20">
                <CardContent className="py-8 text-center">
                  <Clock className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                  <p className="text-muted-foreground">{t("profile.no.waitinglist")}</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {waitingListEvents.map((ev) => {
                  const position = (ev.waitingList || []).indexOf(user.id) + 1;
                  const colorClasses = getEventColor(ev.type);
                  return (
                    <Card 
                      key={ev.id} 
                      className={`border-l-4 transition-all duration-200 cursor-pointer ${colorClasses} opacity-75`}
                      onClick={() => navigate(`/event/${ev.id}`)}
                    >
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <CardTitle className="text-lg hover:text-primary transition-colors">{ev.title}</CardTitle>
                              <Badge variant="outline" className="text-muted-foreground">
                                {language === 'no' ? `Posisjon ${position}` : `Position ${position}`}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-4 text-sm text-muted-foreground">
                              <div className="flex items-center gap-1">
                                <Calendar className="h-4 w-4" />
                                {format(new Date(ev.date), 'PPP')}
                              </div>
                              <div className="flex items-center gap-1">
                                <Clock className="h-4 w-4" />
                                {ev.time.slice(0, 5)}
                              </div>
                            </div>
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            onClick={(e) => {
                              e.stopPropagation();
                              leaveWaitingList(ev.id, user.id);
                            }}
                            className="shrink-0"
                          >
                            {t("event.waitlist.leave")}
                          </Button>
                        </div>
                      </CardHeader>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Penalties Tab */}
        <TabsContent value="penalties" className="space-y-6">
          {!userPenalty || userPenalty.totalDots === 0 ? (
            <Card className="bg-success/5 border-success/20">
              <CardContent className="py-8 text-center">
                <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertTriangle className="h-8 w-8 text-success" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  {language === 'no' ? 'Ren rulleblad!' : 'Clean record!'}
                </h3>
                <p className="text-muted-foreground">
                  {language === 'no' 
                    ? 'Du har ingen aktive straff for øyeblikket.' 
                    : 'You have no active penalties at the moment.'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-destructive/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-destructive" />
                  {language === 'no' ? 'Straff System' : 'Penalty System'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <PenaltyNotice penalty={userPenalty} />
                
                <div className="space-y-2">
                  <h4 className="font-medium">{language === 'no' ? 'Straff Oversikt' : 'Penalty Overview'}:</h4>
                  <div className="flex items-center gap-2">
                    <Badge variant="destructive" className="text-sm">
                      {userPenalty.totalDots} {language === 'no' ? 'prikker' : 'dots'}
                    </Badge>
                    {isPenaltyActive(userPenalty) && (
                      <Badge variant="outline" className="text-sm border-destructive text-destructive">
                        {language === 'no' ? 'Aktiv straff' : 'Active penalty'}
                      </Badge>
                    )}
                  </div>
                </div>

                <div className="space-y-3">
                  <h4 className="font-medium">{language === 'no' ? 'Straff Historikk' : 'Penalty History'}:</h4>
                  <div className="space-y-3">
                    {userPenalty.records.map((record) => (
                      <Card key={record.id} className="bg-muted/30">
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <div className="mt-0.5">
                              {record.type === 'late_cancel' ? (
                                <Clock className="h-4 w-4 text-group-intermediate" />
                              ) : (
                                <XCircle className="h-4 w-4 text-destructive" />
                              )}
                            </div>
                            <div className="flex-1 space-y-1">
                              <div className="flex items-center justify-between">
                                <span className="font-medium text-sm">{record.eventName}</span>
                                <span className="text-xs text-muted-foreground">
                                  {format(new Date(record.timestamp), 'PPP')}
                                </span>
                              </div>
                              <p className="text-sm text-muted-foreground">{formatPenaltyReason(record)}</p>
                              <p className="text-xs text-muted-foreground">
                                {language === 'no' ? 'Arrangement' : 'Event'}: {format(new Date(record.eventDate), 'PPP')}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>

                {isPenaltyActive(userPenalty) && userPenalty.penaltyEndDate && (
                  <Card className="bg-group-intermediate/10 border-group-intermediate/20">
                    <CardContent className="p-4">
                      <p className="text-sm">
                        <strong>{language === 'no' ? 'Straff utløper' : 'Penalty expires'}:</strong>{' '}
                        {format(new Date(userPenalty.penaltyEndDate), 'PPP')}
                      </p>
                    </CardContent>
                  </Card>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Groups Tab */}
        <TabsContent value="groups" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                {language === 'no' ? 'Dine Grupper' : 'Your Groups'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3">
                {myGroups.length ? (
                  myGroups
                    .sort((a, b) => (a.parent_id ? 1 : 0) - (b.parent_id ? 1 : 0))
                    .map((g) => (
                      <Badge key={g.id} variant="secondary" className="justify-start">
                        {g.name}
                      </Badge>
                    ))
                ) : (
                  <p className="text-muted-foreground text-sm">
                    {language === 'no' ? 'Ingen grupper tilgjengelig' : 'No groups available'}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Rating History */}
      <RatingHistoryChart playerId={user.id} height={400} />
    </div>
  );
}
